/**
 * Customer App · Chat with Rigo (Iter 90)
 * -----------------------------------------
 * Rule-based bot + escalation to office staff. HTTP polling every
 * 4 seconds while the tab is visible; pauses when hidden.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  PaperPlaneRight, ChatCircle, User, Warning, Circle,
  Copy,
} from "@phosphor-icons/react";
import { custApi } from "../../lib/customerApi";
import { TranslateDraftButton, TranslateMessage } from "../../components/crm/ChatAssist";
import { RigoAvatar } from "../../components/rigo";
import { linkify } from "../../lib/linkify";

const POLL_MS = 2500;   // Iter 138 — faster live-refresh (was 4000)

export default function MeChat() {
  const [meta, setMeta] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [trOn, setTrOn] = useState(false);
  useEffect(() => { custApi.get("/me/chat/translation-enabled").then(r => setTrOn(!!r.data?.enabled)).catch(() => {}); }, []);
  const [sending, setSending] = useState(false);
  const [showRetention, setShowRetention] = useState(true);
  const scrollRef = useRef(null);
  const endRef = useRef(null);
  const lastAtRef = useRef(null);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const [topPx, setTopPx] = useState(64);
  useEffect(() => {
    const fit = () => { if (boxRef.current) setTopPx(Math.max(0, Math.round(boxRef.current.getBoundingClientRect().top + window.scrollY))); };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);
    window.addEventListener("resize", fit);
    return () => { ro.disconnect(); window.removeEventListener("resize", fit); };
  }, []);

  const loadInitial = useCallback(async () => {
    const [conv, m] = await Promise.all([
      custApi.get("/me/chat/conversation"),
      custApi.get("/me/chat/messages"),
    ]);
    setMeta(conv.data);
    setMsgs(m.data);
    lastAtRef.current = m.data.length ? m.data[m.data.length - 1].created_at : null;
  }, []);

  const poll = useCallback(async () => {
    if (document.hidden) return;
    try {
      const url = lastAtRef.current
        ? `/me/chat/messages?since=${encodeURIComponent(lastAtRef.current)}`
        : "/me/chat/messages";
      const r = await custApi.get(url);
      if (r.data.length) {
        setMsgs(prev => {
          const seen = new Set(prev.map(m => m.id));
          const fresh = r.data.filter(m => !seen.has(m.id));
          return [...prev, ...fresh];
        });
        lastAtRef.current = r.data[r.data.length - 1].created_at;
      }
    } catch (_) { /* ignore transient polling errors */ }
  }, []);

  // Iter 138 — Typing indicator. `staffTyping` shown when staff on the
  // other end is composing. We also POST our own typing state whenever
  // the customer types, throttled to once per 3s.
  const [staffTyping, setStaffTyping] = useState({ active: false, name: "" });
  const typingLastPostRef = useRef(0);
  const pollTyping = useCallback(async () => {
    if (document.hidden) return;
    try {
      const r = await custApi.get("/me/chat/typing");
      setStaffTyping({ active: !!r.data?.staff_typing, name: r.data?.staff_name || "" });
    } catch { /* ignore */ }
  }, []);
  const notifyTyping = useCallback(() => {
    const now = Date.now();
    if (now - typingLastPostRef.current < 3000) return;
    typingLastPostRef.current = now;
    custApi.post("/me/chat/typing").catch(() => {});
  }, []);

  useEffect(() => { loadInitial(); }, [loadInitial]);
  useEffect(() => {
    const t = setInterval(poll, POLL_MS);
    const tt = setInterval(pollTyping, POLL_MS);
    return () => { clearInterval(t); clearInterval(tt); };
  }, [poll, pollTyping]);

  // Iter 112 — Auto-scroll to bottom on new messages. Uses a bottom-anchor
  // sentinel with scrollIntoView so it also works when bubbles have images
  // or wrap onto multiple lines that resize after paint. requestAnimationFrame
  // waits for the browser to commit the layout before we scroll.
  useEffect(() => {
    const jump = () => {
      if (endRef.current) {
        endRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
      } else if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    };
    requestAnimationFrame(jump);
    // Second RAF handles late-arriving fonts/images
    requestAnimationFrame(() => requestAnimationFrame(jump));
  }, [msgs]);

  const send = async () => {
    const t = draft.trim();
    if (!t || sending) return;
    setSending(true);
    // Optimistic push
    const optId = `opt-${Date.now()}`;
    setMsgs(prev => [...prev, {
      id: optId, sender: "customer", sender_name: "You",
      text: t, created_at: new Date().toISOString(),
    }]);
    setDraft("");
    try {
      const r = await custApi.post("/me/chat/messages", { text: t });
      // Iter 158 — swap the optimistic message for the persisted one
      // BEFORE polling for the bot reply. Prevents the double-bubble
      // that happened when the poll fetched the real message under a
      // different id and both rows stayed on-screen.
      const real = r.data?.message;
      setMsgs(prev => {
        const withoutOpt = prev.filter(m => m.id !== optId);
        if (real && !withoutOpt.some(m => m.id === real.id)) {
          return [...withoutOpt, real];
        }
        return withoutOpt;
      });
      if (real?.created_at) lastAtRef.current = real.created_at;
      // Poll immediately to grab the bot / staff reply
      await poll();
    } catch (e) {
      toast.error("Message failed. Try again.");
      setMsgs(prev => prev.filter(m => m.id !== optId));
    } finally {
      setSending(false);
      // Iter 95 — keep composer visible and re-focus for continuous chatting.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  return (
    <div className="flex flex-col bg-white overflow-hidden" ref={boxRef}
         style={{
           /* Subtract whatever sits above (topbar + optional location banner)
              and the fixed bottom nav so the composer stays tappable. */
           height: `calc(100dvh - ${topPx}px - 72px - env(safe-area-inset-bottom))`,
           minHeight: "300px",
         }}
         data-testid="me-chat">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#E5E2D9] bg-[#FEE2E2] shrink-0">
        <div className="flex items-center gap-3">
          <RigoAvatar size={40} />
          <div className="flex-1 min-w-0">
            <div className="font-heading text-base leading-tight">
              {meta?.bot_name || "Rigo"}
            </div>
            <div className="text-[11px] text-[#DC2626] inline-flex items-center gap-1">
              <Circle size={7} weight="fill" className={meta?.in_office_hours ? "text-[#DC2626]" : "text-[#8A8780]"}/>
              {meta?.in_office_hours
                ? "Office team online"
                : `Office ${meta?.office_start}–${meta?.office_end}, bot always on`}
              <span className="mx-1 opacity-40">·</span>
              <span data-testid="me-chat-live-dot"
                    title="Chat auto-refreshes — new replies appear on their own"
                    className="inline-block w-1.5 h-1.5 rounded-full bg-[#DC2626] chat-live-dot"/>
              <span className="text-[10px] uppercase tracking-[0.14em] opacity-70">Live</span>
            </div>
          </div>
        </div>
      </div>

      {/* Retention notice */}
      {showRetention && meta && (
        <div className="mx-4 mt-3 rounded-lg bg-[#DDE7F1] border border-[#B4CDEB] p-3 text-xs text-[#1F3A57] flex items-start gap-2 shrink-0"
             data-testid="me-chat-retention">
          <Warning size={14} weight="fill" className="mt-0.5 shrink-0"/>
          <div className="flex-1">{meta.retention_notice}</div>
          <button onClick={() => setShowRetention(false)}
                  className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#1F3A57] px-1.5">
            Got it
          </button>
        </div>
      )}

      {/* Message list — takes all remaining space above composer */}
      <div ref={scrollRef}
           className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-2"
           data-testid="me-chat-scroll">
        {msgs.length === 0 && (
          <div className="text-center text-[#8A8780] text-sm mt-8">
            <ChatCircle size={30} weight="regular" className="inline-block mb-2 opacity-40"/>
            <div>Ask me anything about your pickups, invoices or bag orders.</div>
          </div>
        )}
        {msgs.map(m => <Bubble key={m.id} m={m} trOn={trOn}/>)}
        {/* Iter 138 — Staff typing indicator */}
        {staffTyping.active && (
          <div className="flex items-center gap-2 text-[11px] text-[#5C5A55] italic"
               data-testid="me-staff-typing">
            <span className="typing-dot-pulse inline-flex gap-0.5">
              <span/><span/><span/>
            </span>
            {staffTyping.name || "Rescigo team"} is typing…
          </div>
        )}
        {/* Bottom sentinel for auto-scroll (Iter 112) */}
        <div ref={endRef} data-testid="me-chat-end"/>
      </div>

      {/* Composer — glued above the message area. shrink-0 stops the
          message list from squishing it; the parent height reserves
          space above the fixed bottom nav. */}
      <div className="border-t border-[#E5E2D9] px-3 py-2 flex items-end gap-2 bg-white shrink-0"
           data-testid="me-chat-composer">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={e => { setDraft(e.target.value); if (e.target.value) notifyTyping(); }}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder="Type a message… (Enter to send)"
          rows={1}
          autoFocus
          data-testid="me-chat-input"
          className="flex-1 min-h-[46px] max-h-[140px] px-3 py-2.5 border border-[#E5E2D9] rounded-2xl text-sm resize-none focus:border-[#DC2626] outline-none"
        />
        {trOn && <TranslateDraftButton draft={draft} setDraft={setDraft} client={custApi} path="/me/chat/translate" />}
        <button onClick={send} disabled={sending || !draft.trim()}
                data-testid="me-chat-send"
                className="h-11 w-11 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-60 text-white flex items-center justify-center shrink-0 shadow-md">
          <PaperPlaneRight size={18} weight="fill"/>
        </button>
      </div>
    </div>
  );
}

function Bubble({ m, trOn }) {
  if (m.kind === "system" || m.sender === "system") {
    return (
      <div className="flex justify-center" data-testid={`me-chat-msg-${m.id}`}>
        <div className="text-[11px] italic text-[#8A8780] text-center max-w-[80%]">
          {linkify(m.text)}
        </div>
      </div>
    );
  }
  const mine = m.sender === "customer";
  const bot  = m.sender === "bot";
  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(m.text || "");
      toast.success("Copied");
    } catch { toast.error("Copy blocked by browser"); }
  };
  return (
    <div className={`flex group ${mine ? "justify-end" : "justify-start"}`}
         data-testid={`me-chat-msg-${m.id}`}>
      {!mine && (
        bot ? (
          <RigoAvatar size={28} className="mr-2 shrink-0" ring={false}/>
        ) : (
          <div className="w-7 h-7 rounded-full flex items-center justify-center mr-2 bg-[#DDE7F1] text-[#1F3A57]">
            <User size={14} weight="fill"/>
          </div>
        )
      )}
      <div className={`relative max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
        mine
          ? "bg-[#DC2626] text-white rounded-br-none"
          : bot
            ? "bg-[#FEE2E2] text-[#7F1D1D] rounded-bl-none border border-[#FEE2E2]"
            : "bg-[#F1F5FA] text-[#15283B] rounded-bl-none border border-[#DDE7F1]"
      }`}>
        <button type="button"
                onClick={copyText}
                data-testid={`me-chat-copy-${m.id}`}
                title="Copy message"
                className={`absolute -top-2 ${mine ? "-left-2" : "-right-2"} opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 rounded-full border shadow-sm inline-flex items-center justify-center ${
                  mine
                    ? "bg-white text-[#DC2626] border-[#FEE2E2] hover:bg-[#FEE2E2]"
                    : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:bg-[#F7F6F2]"
                }`}>
          <Copy size={12} weight="bold"/>
        </button>
        {!mine && (
          <div className={`text-[10px] uppercase tracking-[0.12em] mb-0.5 ${
            bot ? "text-[#DC2626]" : "text-[#1F3A57]"
          }`}>
            {m.sender_name}
          </div>
        )}
        <div className="whitespace-pre-wrap leading-snug">{linkify(m.text)}</div>
        {trOn && !mine && m.text && !String(m.id).startsWith("opt") && <TranslateMessage m={m} targets={["ml"]} client={custApi} path="/me/chat/translate"
          tone={bot ? "text-[#7F1D1D]" : "text-[#1F3A57]"} />}
        <div className={`text-[9px] mt-0.5 opacity-60 ${mine ? "text-white/70" : "text-[#5C5A55]"}`}>
          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}
